import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDebounce } from "@/hooks/use-debounce";
import { DisclosureHost } from "@/hooks/use-disclosure";
import { useCreateInsurancePlanDetails } from "@/queries/insurance/use-create-payer-details";
import { useInsurancePayerList } from "@/queries/use-insurance-payer";
import { PatientInsurancePlanTypeEnum } from "@/services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  showToastOnApiCall,
} from "@/utils/utils";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import CustomSelect from "@/components/CustomSelect";
import { useState } from "react";
import CustomTextArea from "@/components/CustomTextArea";
import Input from "@/components/Input";
import StyledDialog from "@/components/StyledDialog";
import { planTypeMapping } from "../Insurance";
import * as Yup from "yup";
import { useSnackbar } from "@/hooks/useSnackbar";

interface PayerDetailsDrawerProps {
  isEdit?: boolean;
  disclosureHost: DisclosureHost;
  prefillData?: any;
}

const PayerDetailsDrawer = ({
  isEdit,
  disclosureHost,
}: PayerDetailsDrawerProps) => {
  const { mutateAsync: createInsurancePlanDetails } =
    useCreateInsurancePlanDetails();
  const showSnackbar = useSnackbar();
  const initialValue = {
    uuid: undefined,
    payerId: disclosureHost.data ?? undefined,
    planName: undefined,
    planType: undefined,
    insurancePayer: undefined,
    coverageType: undefined,
    note: undefined,
  };

  const queryClient = useQueryClient();
  const [searchInsKey, setSearchInsKey] = useDebounce<string>("");
  const [payerPagination, setPayerPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: insurancePayerList,
    isLoading,
    isFetching,
  } = useInsurancePayerList({
    searchKey: searchInsKey,
    pagination: payerPagination,
  });

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema: Yup.object({
      payerId: Yup.object().required().label("Payer Name"),
      planName: Yup.string()
        .trim()
        .required()
        .label("Plan Name")
        .matches(/[a-zA-Z]/, "Plan Name must contain at least one letter"),
      planType: Yup.string().required().label("Plan Type"),
    }),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  async function handleSubmit(values?: any) {
    const payload = {
      insurancePlan: {
        ...values,
        insurancePayer: {
          payerName: values.payerId.label,
          payerId: values.payerId.value,
        },
      },
      xTENANTID: getXtenantId(),
    };

    if (payload)
      showToastOnApiCall({
        apiCall: createInsurancePlanDetails(payload),
        showSnackbar,
        onSave: () => {
          closeAndResetForm();
          queryClient.invalidateQueries({
            queryKey: ["get-all-insurance-plan"],
            // queryKey: ["insurance-payer-list"],
          });
        },
      });
  }

  function closeAndResetForm() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledDialog
      onClose={disclosureHost.close}
      open={disclosureHost.opened}
      dialogTitle={
        isEdit === true ? "Edit Payer Details" : "View Payer Details"
      }
      dialogAction={
        <Button
          onClick={formik.submitForm}
          variant="contained"
          style={{ width: "80px" }}
        >
          Save
        </Button>
      }
    >
      <Grid
        container
        spacing={2}
        width={{ xs: "36em", md: "52em", xl: "60em" }}
      >
        <Grid item xs={12}>
          <AsyncSelectScroll
            loading={isLoading || isFetching}
            required
            value={formik.values.payerId}
            onChange={(value) => formik.setFieldValue("payerId", value)}
            onSearch={(val) => {
              setSearchInsKey(val);
              setPayerPagination((p) => ({ ...p, page: 0 }));
            }}
            enableFilterOption
            options={insurancePayerList?.data?.content?.map((insurance) => ({
              value: insurance.payerId,
              label: insurance.payerName,
            }))}
            label="Payer Name"
            placeholder="Select"
            error={formik.touched.payerId && (formik.errors.payerId as any)}
            pagination={payerPagination}
            setPagination={setPayerPagination}
            totalElements={insurancePayerList?.data?.page?.totalElements}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            label="Plan Name"
            required
            textFieldProps={{
              name: "planName",
              value: formik.values.planName,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Plan Name",
            }}
            error={formik.touched.planName && formik.errors.planName}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomSelect
            required
            label="Plan Type"
            value={formik.values.planType}
            options={Object.entries(PatientInsurancePlanTypeEnum).map(
              ([key, value]) => ({
                value: value,
                label: key
                  ? key.replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase()
                  : "",
              })
            )}
            onChange={(value) => {
              formik.setFieldValue("planType", value);

              formik.setFieldValue(
                "coverageType",
                planTypeMapping[value ?? ""] || ""
              );
            }}
            placeholder="Enter Plan Type"
            error={formik.touched.planType && formik.errors.planType}
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            label="Coverage Type"
            textFieldProps={{
              disabled: true,
              name: "coverageType",
              value: formik.values.coverageType,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Coverage Type",
            }}
            error={formik.touched.coverageType && formik.errors.coverageType}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextArea
            label="Notes"
            id="outlined-multiline-static"
            rows={3}
            variant="outlined"
            name="notes"
            placeholder="Type Note here"
            onBlur={formik.handleBlur}
            value={formik.values?.note}
            onChange={formik.handleChange}
          />
        </Grid>
      </Grid>
    </StyledDialog>
  );
};

export default PayerDetailsDrawer;
